import React from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';

import Skeleton from '../../../../components/Skeleton';
import Tooltip from '../../../../components/Tooltip';
import useHeatmapConfig from '../../hooks/useHeatmapConfig';
import { format, endOfMonth, isSame } from '../../../../utils/date';
import { initMaxDate } from '../../configs/chart-config';
import { formatValue } from '../../utils/heatmap';

const SectorsHeatmapTable = ({ months, data }) => {
  const { yearGroups, lastMonthIndex, getBackground } = useHeatmapConfig({ months, data });

  return (
    <table className="ai-dashboard-heatmap sectors-heatmap">
      <thead>
        <tr className="ai-dashboard-heatmap__year-row">
          <th />
          {yearGroups.map(({ year, count }) => (
            <th key={year} colSpan={count}>
              {year}
            </th>
          ))}
        </tr>
        <tr className="ai-dashboard-heatmap__month-row">
          <th>
            <div className="d-flex align-items-center justify-content-between">
              <div>Sector</div>
              <div className="d-flex">
                % of Time Spent{' '}
                <Tooltip
                  content="Share of total mobile app time spent in each sector by this user segment"
                  placement="top"
                  colorVariant="light"
                >
                  <span className="hicon-question-circle" />
                </Tooltip>
              </div>
            </div>
          </th>
          {months.map((date, i) => (
            <th key={date}>
              {i === lastMonthIndex && !isSame(initMaxDate, endOfMonth(date)) ? (
                <div className="d-flex align-items-center">
                  {format(date, 'MMM')} (partial){' '}
                  <Tooltip content="Partial month" placement="top" colorVariant="light">
                    <span className="hicon-question-circle" />
                  </Tooltip>
                </div>
              ) : (
                format(date, 'MMM')
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((sector) => (
          <tr key={sector.name}>
            <td className="sector-lead-cell">
              <div className="sector-lead-cell__content">
                <div className="sector-lead-cell__name text-truncate">{sector.name}</div>
                <div className="sector-lead-cell__time-spent">
                  {formatValue(sector.time_spent_pct)}
                </div>
              </div>
            </td>
            {sector.values.map((value, i) => {
              const isPartialCell =
                i === sector.values.length - 1 && !isSame(initMaxDate, endOfMonth(months[i]));
              return (
                <td
                  key={months[i]}
                  style={{ backgroundColor: getBackground(value) }}
                  className={cx({ 'is-partial': isPartialCell })}
                >
                  {formatValue(value)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const emptyArray = [];
const SectorsHeatmap = ({ months, data, isPending }) => {
  if (isPending) {
    return (
      <div className="d-flex p-mdl">
        <Skeleton height={640} />
      </div>
    );
  }

  if (isEmpty(months) || isEmpty(data)) {
    return (
      <div className="d-flex p-mdl">
        <div className="ai-dashboard-heatmap__empty flex-grow">No data available</div>
      </div>
    );
  }

  return <SectorsHeatmapTable months={months || emptyArray} data={data || emptyArray} />;
};

// Shared PropTypes
const MonthsPropType = T.arrayOf(T.string);

const SectorPropType = T.shape({
  name: T.string,
  time_spent_pct: T.number,
  values: T.arrayOf(T.number),
});

const SectorsHeatmapTableProps = {
  months: MonthsPropType,
  data: T.arrayOf(SectorPropType),
};

SectorsHeatmapTable.propTypes = SectorsHeatmapTableProps;
SectorsHeatmap.propTypes = {
  ...SectorsHeatmapTableProps,
  isPending: T.bool.isRequired,
};

export default SectorsHeatmap;
